import { useEffect, useState, type KeyboardEvent, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib';
import { PlainField } from '@shared/ui';
import type { AnswerOutcome, AnswerPhase, ExerciseKind } from '../../model';
import { ANSWER_MAX_CHARS, type MatchedVia } from '../../api/sessionApi';
import type { SessionVariant } from '../variant';

interface AnswerDockProps {
  exercise: ExerciseKind;
  phase: AnswerPhase;
  /** Ответ отправлен и ждёт вердикта (не переход к следующей карточке и не «завершить»). */
  checking: boolean;
  outcome: AnswerOutcome | null;
  matchedVia: MatchedVia | null;
  gaveUp: boolean;
  hintOpen: boolean;
  hasHint: boolean;
  awaitingContinue: boolean;
  autoAdvanceMs: number | null;
  typed: string;
  onTyped: (value: string) => void;
  onSubmit: () => void;
  onGiveUp: () => void;
  onHint: () => void;
  onContinue: () => void;
  inputRef: RefObject<HTMLDivElement>;
  variant: SessionVariant;
  /** Прямой перевод по предложению — в поле «перевод выделенного слова». */
  wordInSentence?: boolean;
}

/** Через сколько после отправки без вердикта показать «Проверяем ответ…». */
const SLOW_CHECK_MS = 1200;

/** «Проверяем ответ…», если вердикт задержался (смысловая проверка — 3–4 с, ТЗ демо 1.7). */
function useSlowCheck(checking: boolean): boolean {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!checking) {
      setSlow(false);
      return;
    }
    const id = window.setTimeout(() => setSlow(true), SLOW_CHECK_MS);
    return () => window.clearTimeout(id);
  }, [checking]);
  return slow && checking;
}

/**
 * Нижний док сцены. Одна плашка на все состояния: поле ввода → проверка →
 * вердикт → «Продолжить». На десктопе это одна пилюля (инструменты внутри неё),
 * на мобиле поле и инструменты разнесены по двум рядам — под большой палец.
 */
export function AnswerDock(p: AnswerDockProps) {
  const { t } = useTranslation();
  const isMobile = p.variant === 'mobile';
  const isEnglishInput = p.exercise === 'reverse' || p.exercise === 'cloze';
  const isMcq = p.exercise === 'mcq';
  const answered = p.phase === 'feedback';
  const submitting = p.phase === 'submitting';
  const good = p.outcome === 'good';
  const slow = useSlowCheck(p.checking && !answered);
  // Подпись — на месте ряда «не помню · подсказка» (телефон) или строки клавиш (компьютер); диктору —
  // через постоянную live-область (вставленный сразу с текстом узел читается не везде).
  const checkingLabel = slow ? (
    <div className="st-checking" aria-hidden>
      {t('session.checkingLong')}
    </div>
  ) : null;
  const checkingLive = (
    <span className="sr-only" aria-live="polite">
      {slow ? t('session.checkingLong') : ''}
    </span>
  );

  const placeholder = isEnglishInput
    ? t('session.typeWordEn', 'введи слово на английском…')
    : p.wordInSentence
      ? t('session.typeTranslationWord')
      : t('session.typeTranslation', 'введи перевод…');
  const hintLabel =
    p.exercise === 'reverse'
      ? t('session.hint.firstLetter', 'первая буква')
      : t('session.hint.show', 'подсказка');

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      p.onSubmit();
    }
  };

  const giveUpBtn = (
    <button type="button" className="st-ghost" onClick={p.onGiveUp} disabled={submitting}>
      {t('session.dontKnow', 'не помню')}
    </button>
  );
  const hintBtn = p.hasHint ? (
    <button
      type="button"
      className={cn('st-hint-btn', p.hintOpen && 'is-open')}
      onClick={p.onHint}
      disabled={submitting}
    >
      {hintLabel}
      {!isMobile && <kbd>Tab</kbd>}
    </button>
  ) : null;

  // ── вердикт ─────────────────────────────────────────────────────────────
  if (answered) {
    const readout = p.gaveUp ? '—' : p.typed;
    // Серию в вердикте не дублируем: она и так живёт в плашке стрика сверху.
    const verdict = good
      ? p.matchedVia === 'typo'
        ? t('session.verdict.typo')
        : p.matchedVia === 'semantic'
          ? t('session.verdict.semantic')
          : t('session.verdict.ok')
      : p.gaveUp
        ? t('session.verdict.gaveUp')
        : t('session.verdict.wrong');

    const result = (
      <div className="st-result">
        <span className={cn('st-glyph', good ? 'is-good' : 'is-bad')} aria-hidden>
          {good ? '✓' : '✗'}
        </span>
        <span className="st-result-body">
          {!isMcq && (
            <span className={cn('st-readout', !good && !p.gaveUp && 'is-struck')}>{readout}</span>
          )}
          <span className="st-verdict">{verdict}</span>
        </span>
      </div>
    );
    const continueBtn = p.awaitingContinue ? (
      <button
        type="button"
        className={cn('st-continue', p.autoAdvanceMs && 'is-timed')}
        style={p.autoAdvanceMs ? ({ ['--ms' as string]: `${p.autoAdvanceMs}ms` }) : undefined}
        onClick={p.onContinue}
      >
        {t('session.feedback.continue', 'Продолжить')}
        {!isMobile && <kbd>Enter</kbd>}
      </button>
    ) : null;

    return isMobile ? (
      <div className="st-dock">
        <div className="st-bar is-result">{result}</div>
        {continueBtn}
      </div>
    ) : (
      <div className="st-dock">
        <div className={cn('st-bar', 'is-result', good ? 'tone-good' : 'tone-bad')}>
          {result}
          {continueBtn}
        </div>
      </div>
    );
  }

  // ── выбор из четырёх: поля ввода нет, только подсказка ──────────────────
  if (isMcq) {
    return (
      <div className="st-dock">
        {checkingLive}
        {hintBtn && <div className="st-tools is-center">{slow && isMobile ? checkingLabel : hintBtn}</div>}
        {!isMobile && (slow ? checkingLabel : <div className="st-legend">{t('session.legend.mcq')}</div>)}
      </div>
    );
  }

  // ── ввод ────────────────────────────────────────────────────────────────
  const sendBtn = (
    <button
      type="button"
      className={cn('st-send', p.typed.trim() && 'is-ready')}
      onClick={p.onSubmit}
      disabled={submitting || !p.typed.trim()}
      aria-label={t('session.checkBtn', 'Проверить')}
    >
      {submitting ? <span className="st-spinner" aria-hidden /> : '↵'}
    </button>
  );

  return (
    <div className="st-dock">
      {checkingLive}
      <div className="st-bar">
        <PlainField
          ref={p.inputRef}
          value={p.typed}
          onChange={p.onTyped}
          onKeyDown={onKey}
          disabled={submitting}
          autoCapitalize="off"
          spellCheck={false}
          lang={isEnglishInput ? 'en' : 'ru'}
          inputMode="text"
          maxLength={ANSWER_MAX_CHARS}
          placeholder={placeholder}
          ariaLabel={placeholder}
          className="st-input"
        />
        {!isMobile && giveUpBtn}
        {!isMobile && hintBtn}
        {sendBtn}
      </div>
      {isMobile && (
        // На время ожидания ряд показывает только подпись по центру: кнопки и так неактивны, а между ними
        // на 320 px подпись не помещается. Высота ряда та же (44 px).
        <div className={cn('st-tools', slow && 'is-center')}>
          {slow ? (
            checkingLabel
          ) : (
            <>
              {giveUpBtn}
              {hintBtn}
            </>
          )}
        </div>
      )}
      {!isMobile &&
        (slow ? (
          checkingLabel
        ) : (
          <div className="st-legend">
            {t(p.exercise === 'reverse' ? 'session.legend.reverse' : 'session.legend.typed')}
          </div>
        ))}
    </div>
  );
}
